import {Record, List} from 'immutable';

import {markConversationAsRead} from 'web-app/react/inbox/conversation-center/actions';
import {markAsUnreadSuccess} from 'web-app/react/inbox/conversation/actions';
import {makeReducer, match} from 'web-app/util/redux';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import * as Actions from './actions';
import type * as Types from './types';
import {markAllNotificationsAsSeenSuccess} from '../notification-center/actions';

const ConversationCount = Record({
    unread: 0,
    total: 0,
});

const Conversation = Record({
    ALL: ConversationCount(),
    OWN: ConversationCount(),
    STAR: ConversationCount(),
    userContexts: {} as Types.UserContextsConversations,
});

const conversationFromApi = (data: Types.UpdatesFromREST['conversations']) =>
    new Conversation({
        ...data,
        ALL: new ConversationCount(data.ALL),
        OWN: new ConversationCount(data.OWN),
        STAR: new ConversationCount(data.STAR),
        userContexts: data.userContexts ?? {},
    });

const Notifications = Record({
    unseen: 0,
    userContexts: {} as Types.UserContextsNotifications,
});

const notificationCountsFromApi = (data: Types.UpdatesFromREST) =>
    Record({
        ...data,
        conversations: conversationFromApi(data.conversations),
        notifications: new Notifications({
            unseen: data.notifications.unseen,
            userContexts: data.notifications.userContexts ?? {},
        }),
        warnings: List(data.warnings),
    })();

const NotificationCountsState = Record({
    conversations: Conversation(),
    notifications: Notifications(),
    warnings: List(),
});

export type NotificationCountsStateType = ReturnType<typeof NotificationCountsState>;

export default makeReducer<NotificationCountsStateType>(
    [
        match(Actions.getNotificationCountSuccess, (state, {payload}) => notificationCountsFromApi(payload.response)),
        match(markConversationAsRead, state =>
            state.merge({
                conversations: state.conversations.merge({
                    OWN: state.conversations.OWN.merge({
                        unread: state.conversations.OWN.unread ? state.conversations.OWN.unread - 1 : 0,
                    }),
                }),
            }),
        ),
        match(markAsUnreadSuccess, state =>
            state.merge({
                conversations: state.conversations.merge({
                    OWN: state.conversations.OWN.merge({
                        unread: hasValue(state.conversations.OWN.unread) ? state.conversations.OWN.unread + 1 : 0,
                    }),
                }),
            }),
        ),
        // Optimistically update the unread count
        match(markAllNotificationsAsSeenSuccess, state =>
            state.merge({notifications: state.notifications.merge({unseen: 0})}),
        ),
    ],
    new NotificationCountsState(),
);
